// frontend/src/components/layout/VeloceStatusBadge.tsx
// Statut de connexion à la caisse Véloce dans l'en-tête : un coup d'œil, et
// un clic mène aux paramètres de connexion quand la connexion n'est pas
// active. Le statut vient du serveur (identifiants résolus pour l'emplacement
// courant + dernière vérification réelle), rafraîchi chaque minute.

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Cloud, CloudOff, CloudAlert } from 'lucide-react';
import useLocationStore from '../../stores/locationStore';
import { fetchVeloceStatus, type VeloceConnectionStatus } from '../../services/veloceService';
import { cn } from '../ui/utils';

export const VELOCE_STATUS_QUERY_KEY = ['veloce', 'status'] as const;

type Tone = 'ok' | 'warn' | 'off' | 'loading';

function toneOf(status: VeloceConnectionStatus | undefined, isError: boolean): { tone: Tone; label: string; title: string } {
  if (isError) return { tone: 'warn', title: 'Statut Véloce indisponible — cliquez pour vérifier la connexion.', label: 'Véloce ?' };
  if (!status) return { tone: 'loading', title: 'Vérification de la connexion Véloce…', label: 'Véloce' };
  if (!status.configured) {
    return { tone: 'off', label: 'Véloce non connecté', title: 'Aucun identifiant Véloce enregistré — cliquez pour configurer la connexion.' };
  }
  if (status.connected) {
    const when = status.lastCheckedAt ? new Date(status.lastCheckedAt).toLocaleString('fr-CA') : '';
    return { tone: 'ok', label: 'Véloce connecté', title: `Connecté à Véloce Cloud (${status.email})${when ? ` — vérifié le ${when}` : ''}.` };
  }
  if (status.lastStatus === 'FAILED') {
    return { tone: 'warn', label: 'Véloce en erreur', title: `La dernière vérification a échoué : ${status.lastError ?? 'erreur inconnue'}. Cliquez pour corriger.` };
  }
  return { tone: 'warn', label: 'Véloce à vérifier', title: 'Identifiants enregistrés mais jamais vérifiés — cliquez pour vérifier la connexion.' };
}

export function VeloceStatusBadge() {
  const navigate = useNavigate();
  const currentLocation = useLocationStore((s) => s.currentLocation);
  const { data, isError } = useQuery({
    queryKey: [...VELOCE_STATUS_QUERY_KEY, currentLocation?.id ?? null],
    queryFn: fetchVeloceStatus,
    enabled: !!currentLocation,
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: 1,
  });
  if (!currentLocation) return null;

  const { tone, label, title } = toneOf(data, isError);
  const Icon = tone === 'ok' ? Cloud : tone === 'off' ? CloudOff : CloudAlert;

  return (
    <button
      type="button"
      onClick={() => navigate('/parametres?tab=veloce')}
      title={title}
      aria-label={label}
      className={cn(
        'flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium transition-colors min-h-[36px]',
        tone === 'ok' && 'text-[var(--color-olive)] hover:bg-[var(--color-olive-bg)]',
        tone === 'warn' && 'text-[var(--color-amber-dark)] bg-[var(--color-amber-bg)] hover:opacity-90',
        tone === 'off' && 'text-[var(--color-brick)] bg-[var(--color-brick-bg)] hover:opacity-90',
        tone === 'loading' && 'text-[var(--muted-foreground)]'
      )}
    >
      <Icon className={cn('w-4 h-4', tone === 'loading' && 'animate-pulse')} />
      <span className="hidden md:inline">{label}</span>
    </button>
  );
}
